import { Component } from "@theme/component";
import { CartAddEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

/**
 * @typedef {Object} BundleSelection
 * @property {string} variantId
 * @property {string} productTitle
 * @property {string} variantTitle
 * @property {string} productId
 * @property {string} imageUrl
 * @property {number} price - Price in cents
 * @property {number} compareAtPrice - Compare at price in cents
 */

/**
 * @typedef {Object} FtsBundleBuilderRefs
 * @property {HTMLElement} productsContainer
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} validationMessage
 * @property {HTMLElement} originalPrice
 * @property {HTMLElement} comparePrice
 * @property {HTMLElement} salePrice
 * @property {HTMLElement} saveBadge
 * @property {HTMLElement} footerOriginal
 * @property {HTMLElement} footerPrice
 * @property {HTMLElement} footer
 */

/**
 * FTS Bundle Builder Component - Comfrt Style
 * @extends {Component<FtsBundleBuilderRefs>}
 */
class FtsBundleBuilder extends Component {
  /** @type {Map<number, BundleSelection>} */
  #selections = new Map();

  /** @type {Map<number, { productId: string, reason: string }>} */
  #invalidSelections = new Map();

  /** @type {Map<string, Array>} "step-productId" -> variants */
  #variantData = new Map();

  /** @type {Map<string, Record<string, {q:number,p:string}>>} "step-productId" -> { variantId: {q,p} } */
  #stockData = new Map();

  /** @type {number} Total number of products in bundle */
  #totalProducts = 0;

  connectedCallback() {
    super.connectedCallback();

    const renderedProducts = this.querySelectorAll(
      ".fts-bundle__product",
    ).length;
    const dataTotalProducts = parseInt(this.dataset.totalProducts || "", 10);

    this.#totalProducts =
      renderedProducts ||
      (Number.isNaN(dataTotalProducts) ? 0 : dataTotalProducts);

    this.#normalizeStepIndices();
    this.#loadVariantData();
    this.#loadStockData();
    this.#initializeSelections({ updateUi: true });
    this.#updatePriceDisplay();
    this.#updateButtonState();
  }

  /**
   * Load variant JSON data for each product
   */
  #loadVariantData() {
    const variantScripts = this.querySelectorAll("script[data-variants]");
    for (const script of variantScripts) {
      const key = script.dataset.variants;
      try {
        const variants = JSON.parse(script.textContent || "[]");
        this.#variantData.set(key, variants);
      } catch (error) {
        console.error(`Failed to parse variants for ${key}`, error);
      }
    }
  }

  /**
   * Load the companion per-variant stock map for each product. Keyed by the same
   * normalized "step-productId" as #variantData so the two line up.
   */
  #loadStockData() {
    const stockScripts = this.querySelectorAll("script[data-variant-stock]");
    for (const script of stockScripts) {
      const key = script.dataset.variantStock;
      if (!key) continue;
      try {
        this.#stockData.set(key, JSON.parse(script.textContent || "{}"));
      } catch (error) {
        console.error(`Failed to parse stock for ${key}`, error);
      }
    }
  }

  /**
   * Normalize rendered product step keys to contiguous numeric values.
   * This lets the builder support dynamic child blocks that initially render
   * with block IDs instead of slot-based numeric keys.
   */
  #normalizeStepIndices() {
    const productContainers = this.querySelectorAll(".fts-bundle__product");
    let normalizedStep = 0;

    for (const productContainer of productContainers) {
      if (!(productContainer instanceof HTMLElement)) continue;

      const productId = productContainer.dataset.productId;
      if (!productId) continue;

      normalizedStep += 1;
      const stepValue = String(normalizedStep);
      productContainer.dataset.step = stepValue;

      const stepNodes = productContainer.querySelectorAll("[data-step]");
      for (const node of stepNodes) {
        if (!(node instanceof HTMLElement)) continue;
        node.dataset.step = stepValue;
      }

      const priceContainer = productContainer.querySelector(
        "[data-product-price]",
      );
      if (priceContainer instanceof HTMLElement) {
        priceContainer.dataset.productPrice = `${stepValue}-${productId}`;
      }

      const variantScript = productContainer.querySelector(
        "script[data-variants]",
      );
      if (variantScript instanceof HTMLScriptElement) {
        variantScript.dataset.variants = `${stepValue}-${productId}`;
      }

      const stockScript = productContainer.querySelector(
        "script[data-variant-stock]",
      );
      if (stockScript instanceof HTMLScriptElement) {
        stockScript.dataset.variantStock = `${stepValue}-${productId}`;
      }
    }
  }

  /**
   * Initialize all selections from the rendered UI
   * @param {{ updateUi?: boolean }} [options]
   */
  #initializeSelections({ updateUi = true } = {}) {
    this.#selections.clear();
    this.#invalidSelections.clear();
    this.#initializeAutoSelections();

    // Give every copy of a product the same in-stock default so duplicate line
    // items don't all land on the merchant's first-available variant when it's
    // too scarce to supply all of them (the original "Wine Red / 3XL" 422).
    for (const [productId, steps] of this.#getMultiVariantProductSteps()) {
      this.#applyUniformDefault(productId, steps);
    }

    const productContainers = this.querySelectorAll(".fts-bundle__product");
    for (const productContainer of productContainers) {
      if (!(productContainer instanceof HTMLElement)) continue;

      const step = parseInt(productContainer.dataset.step || "", 10);
      const productId = productContainer.dataset.productId;
      const hasOptions = productContainer.querySelector('input[type="radio"]');

      if (Number.isNaN(step) || !productId) continue;

      if (!hasOptions) {
        this.#clearInvalidSelection(step, productContainer);
        if (updateUi && this.#selections.has(step)) {
          this.#updateProductPriceDisplay(step, productId);
        }
        continue;
      }

      this.#refreshOptionAvailability(step, productId, productContainer);
      this.#syncProductState(step, productId, { updateUi });
    }

    this.#updateAccentColor();
  }

  /**
   * Auto-select single variant products
   */
  #initializeAutoSelections() {
    const autoSelected = this.querySelectorAll('[data-auto-selected="true"]');

    for (const input of autoSelected) {
      if (!(input instanceof HTMLInputElement)) continue;

      const step = parseInt(input.dataset.step || "", 10);
      const productId = input.dataset.productId;
      const variantId = input.dataset.variantId;
      const variantPrice = parseInt(input.dataset.variantPrice || "", 10) || 0;
      const variantCompareAtPrice =
        parseInt(input.dataset.variantCompareAtPrice || "", 10) || variantPrice;
      const productContainer = input.closest(".fts-bundle__product");
      const productTitle =
        productContainer
          ?.querySelector(".fts-bundle__product-title")
          ?.textContent?.trim() || "";

      if (Number.isNaN(step) || !variantId) continue;

      this.#selections.set(step, {
        variantId,
        productTitle,
        variantTitle: "Default",
        productId: productId || "",
        imageUrl: "",
        price: variantPrice,
        compareAtPrice: variantCompareAtPrice,
      });
    }
  }

  /**
   * Handle option change (radio button selection)
   * @param {Event} event
   */
  handleOptionChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const step = parseInt(event.target.dataset.step || "", 10);
    const productId = event.target.dataset.productId;

    const fieldset = event.target.closest("fieldset");
    this.#updateSelectedValueDisplay(fieldset);

    if (Number.isNaN(step) || !productId) return;

    const productContainer = this.#getProductContainer(step, productId);
    if (!(productContainer instanceof HTMLElement)) return;

    this.#refreshOptionAvailability(step, productId, productContainer);
    this.#syncProductState(step, productId);
    // This pick changes how much stock the other copies of the same product can
    // still claim — re-grey (or re-enable) their options live.
    this.#refreshSiblingAvailability(step, productId);
    this.#updateAccentColor();
  }

  /**
   * Re-evaluate option availability for every OTHER copy of a product after one
   * copy's selection changes, so a scarce variant greys out once it's claimed
   * (and un-greys when freed). Demand is read from the live #selections.
   * @param {number} changedStep
   * @param {string} productId
   */
  #refreshSiblingAvailability(changedStep, productId) {
    const containers = this.querySelectorAll(
      `.fts-bundle__product[data-product-id="${productId}"]`,
    );
    for (const container of containers) {
      if (!(container instanceof HTMLElement)) continue;
      const step = parseInt(container.dataset.step || "", 10);
      if (Number.isNaN(step) || step === changedStep) continue;
      this.#refreshOptionAvailability(step, productId, container);
      this.#syncProductState(step, productId);
    }
  }

  /**
   * Sync bundle accent color to the first product's selected color swatch
   */
  #updateAccentColor() {
    const accentSwatch = this.#getAccentSourceSwatch();
    if (!(accentSwatch instanceof HTMLElement)) {
      this.#clearAccentColor();
      return;
    }

    const accentColor = this.#extractAccentColorFromSwatch(accentSwatch);
    if (!accentColor) {
      this.#clearAccentColor();
      return;
    }

    this.#setAccentColor({
      background: `rgb(${accentColor.r}, ${accentColor.g}, ${accentColor.b})`,
      foreground: this.#getAccessibleForeground(accentColor),
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getFirstProductContainer() {
    return this.querySelector('.fts-bundle__product[data-step="1"]');
  }

  /**
   * @param {HTMLElement} productContainer
   * @returns {HTMLElement | null}
   */
  #getPreferredColorGroup(productContainer) {
    const swatchGroups = Array.from(
      productContainer.querySelectorAll(".fts-bundle__option-group--swatches"),
    ).filter((group) => group instanceof HTMLElement);

    if (!swatchGroups.length) return null;

    const preferredGroup = swatchGroups.find((group) => {
      const legendText = group.querySelector("legend")?.textContent || "";
      return /core color|color/i.test(legendText);
    });

    return preferredGroup || swatchGroups[0] || null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getAccentSourceSwatch() {
    const firstProductContainer = this.#getFirstProductContainer();
    if (!(firstProductContainer instanceof HTMLElement)) return null;

    const preferredColorGroup = this.#getPreferredColorGroup(
      firstProductContainer,
    );
    if (!(preferredColorGroup instanceof HTMLElement)) return null;

    const selectedSwatch = preferredColorGroup.querySelector(
      'input[type="radio"]:checked + .swatch',
    );

    return selectedSwatch instanceof HTMLElement ? selectedSwatch : null;
  }

  /**
   * @param {HTMLElement} swatchEl
   * @returns {{ r: number, g: number, b: number } | null}
   */
  #extractAccentColorFromSwatch(swatchEl) {
    const computedStyles = getComputedStyle(swatchEl);
    const candidates = [
      computedStyles.backgroundColor,
      swatchEl.style.getPropertyValue("--swatch-background"),
      computedStyles.getPropertyValue("--swatch-background"),
    ];

    for (const candidate of candidates) {
      const rgb = this.#extractRgbComponents(candidate);
      if (rgb) return rgb;
    }

    return null;
  }

  /**
   * @param {string} cssColor
   * @returns {{ r: number, g: number, b: number } | null}
   */
  #extractRgbComponents(cssColor) {
    if (!cssColor) return null;

    const normalizedColor = cssColor.trim();
    if (!normalizedColor || normalizedColor === "transparent") return null;

    const hexMatch = normalizedColor.match(/^#([\da-f]{3,8})$/i);
    if (hexMatch) {
      const hexValue = hexMatch[1];
      const channelSize = hexValue.length <= 4 ? 1 : 2;
      const expand = (value) =>
        channelSize === 1 ? `${value}${value}` : value;
      const parseChannel = (startIndex) =>
        parseInt(
          expand(hexValue.slice(startIndex, startIndex + channelSize)),
          16,
        );

      const r = parseChannel(0);
      const g = parseChannel(channelSize);
      const b = parseChannel(channelSize * 2);
      const alphaIndex = channelSize * 3;
      const alphaValue =
        hexValue.length === 4 || hexValue.length === 8
          ? parseChannel(alphaIndex) / 255
          : 1;

      if (alphaValue <= 0) return null;

      return { r, g, b };
    }

    const rgbMatch = normalizedColor.match(
      /^rgba?\(\s*([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:\s*,\s*|\s+)([\d.]+)(?:\s*(?:,|\/)\s*([\d.]+))?\s*\)$/i,
    );

    if (!rgbMatch) return null;

    const alphaValue = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;
    if (!Number.isFinite(alphaValue) || alphaValue <= 0) return null;

    const clampChannel = (value) =>
      Math.max(0, Math.min(255, Math.round(parseFloat(value))));

    return {
      r: clampChannel(rgbMatch[1]),
      g: clampChannel(rgbMatch[2]),
      b: clampChannel(rgbMatch[3]),
    };
  }

  /**
   * @param {{ r: number, g: number, b: number }} rgb
   * @returns {string}
   */
  #getAccessibleForeground(rgb) {
    const toLinear = (value) => {
      const channel = value / 255;
      return channel <= 0.03928
        ? channel / 12.92
        : ((channel + 0.055) / 1.055) ** 2.4;
    };

    const luminance =
      0.2126 * toLinear(rgb.r) +
      0.7152 * toLinear(rgb.g) +
      0.0722 * toLinear(rgb.b);

    return luminance > 0.45 ? "#111111" : "#ffffff";
  }

  /**
   * @param {string} foreground
   * @returns {string}
   */
  #getMutedForeground(foreground) {
    return foreground === "#ffffff"
      ? "rgba(255, 255, 255, 0.72)"
      : "rgba(17, 17, 17, 0.62)";
  }

  /**
   * @param {{ background: string, foreground: string }} colors
   */
  #setAccentColor({ background, foreground }) {
    this.style.setProperty("--bundle-accent-bg", background);
    this.style.setProperty("--bundle-accent-fg", foreground);
    this.style.setProperty(
      "--bundle-accent-muted-fg",
      this.#getMutedForeground(foreground),
    );
  }

  #clearAccentColor() {
    this.style.removeProperty("--bundle-accent-bg");
    this.style.removeProperty("--bundle-accent-fg");
    this.style.removeProperty("--bundle-accent-muted-fg");
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @returns {HTMLElement | null}
   */
  #getProductContainer(step, productId) {
    const selector = `.fts-bundle__product[data-step="${step}"][data-product-id="${productId}"]`;
    return this.querySelector(selector);
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {HTMLElement} productContainer
   */
  #refreshOptionAvailability(step, productId, productContainer) {
    const optionGroups = Array.from(
      productContainer.querySelectorAll(".fts-bundle__option-group"),
    ).filter((group) => group instanceof HTMLElement);

    if (!optionGroups.length) return;

    let shouldRecompute = true;
    let remainingPasses = optionGroups.length + 1;

    while (shouldRecompute && remainingPasses > 0) {
      shouldRecompute = false;
      remainingPasses -= 1;

      for (const optionGroup of optionGroups) {
        if (
          this.#updateOptionGroupAvailability(
            step,
            productId,
            productContainer,
            optionGroup,
          )
        ) {
          shouldRecompute = true;
        }
      }
    }
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {HTMLElement} productContainer
   * @param {HTMLElement} optionGroup
   * @returns {boolean}
   */
  #updateOptionGroupAvailability(
    step,
    productId,
    productContainer,
    optionGroup,
  ) {
    let selectionChanged = false;

    const isSwatchGroup = optionGroup.classList.contains(
      "fts-bundle__option-group--swatches",
    );
    const optionInputs = optionGroup.querySelectorAll(
      '.fts-bundle__option-label input[type="radio"]',
    );

    for (const input of optionInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const optionLabel = input.closest(".fts-bundle__option-label");
      if (!(optionLabel instanceof HTMLElement)) continue;

      const { isAvailable, isVisible } = this.#getOptionAvailability(
        step,
        productId,
        productContainer,
        input,
        isSwatchGroup,
      );

      if (
        this.#applyOptionAvailability(optionLabel, input, {
          isAvailable,
          isVisible,
        })
      ) {
        selectionChanged = true;
      }
    }

    this.#updateSelectedValueDisplay(optionGroup);

    return selectionChanged;
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {HTMLElement} productContainer
   * @param {HTMLInputElement} input
   * @param {boolean} isSwatchGroup
   * @returns {{ isAvailable: boolean, isVisible: boolean }}
   */
  #getOptionAvailability(
    step,
    productId,
    productContainer,
    input,
    isSwatchGroup,
  ) {
    const optionPosition = parseInt(input.dataset.optionPosition || "", 10);

    if (Number.isNaN(optionPosition)) {
      return {
        isAvailable: !input.disabled,
        isVisible: true,
      };
    }

    const selectedOptions = this.#getSelectedOptionMap(productContainer);
    selectedOptions.set(optionPosition, input.value);

    return {
      // Swatch (colour) values are judged independently of the other dimensions
      // so a colour stays selectable as long as it has any in-stock variant;
      // non-swatch (size) values narrow against the full current selection.
      isAvailable: isSwatchGroup
        ? this.#hasPurchasableVariantForOptionValue(
            step,
            productId,
            optionPosition,
            input.value,
          )
        : this.#hasMatchingAvailableVariant(step, productId, selectedOptions),
      isVisible: isSwatchGroup
        ? this.#hasAvailableOptionValue(
            step,
            productId,
            optionPosition,
            input.value,
          )
        : true,
    };
  }

  /**
   * @param {HTMLElement} optionLabel
   * @param {HTMLInputElement} input
   * @param {{ isAvailable: boolean, isVisible: boolean }} options
   * @returns {boolean}
   */
  #applyOptionAvailability(optionLabel, input, { isAvailable, isVisible }) {
    let selectionChanged = false;
    const shouldDisable = !isVisible || !isAvailable;

    optionLabel.dataset.unavailable = String(!isAvailable);
    optionLabel.hidden = !isVisible;
    input.disabled = shouldDisable;

    if (!shouldDisable) {
      input.removeAttribute("aria-disabled");
    } else {
      input.setAttribute("aria-disabled", "true");
    }

    if (shouldDisable && input.checked) {
      input.checked = false;
      selectionChanged = true;
    }

    return selectionChanged;
  }

  /**
   * @param {HTMLElement | null} fieldset
   */
  #updateSelectedValueDisplay(fieldset) {
    if (!(fieldset instanceof HTMLElement)) return;

    const selectedValueSpan = fieldset.querySelector("[data-option-selected]");
    if (!(selectedValueSpan instanceof HTMLElement)) return;

    const checkedInput = Array.from(
      fieldset.querySelectorAll('input[type="radio"]:checked'),
    ).find(
      (input) => input instanceof HTMLInputElement && input.disabled === false,
    );

    selectedValueSpan.textContent =
      checkedInput instanceof HTMLInputElement ? checkedInput.value : "";
  }

  /**
   * How many units of a variant the OTHER copies of a product have already
   * claimed. A B1G2F bundle posts each copy as its own quantity-1 line item and
   * Shopify sums same-variant lines against stock, so this is the live demand a
   * candidate must leave room on top of.
   * @param {number} excludeStep - the copy being evaluated (not counted)
   * @param {string} productId
   * @param {string|number} variantId
   * @returns {number}
   */
  #committedDemandForVariant(excludeStep, productId, variantId) {
    let count = 0;
    for (const [selectionStep, selection] of this.#selections) {
      if (selectionStep === excludeStep) continue;
      if (
        selection.productId === productId &&
        selection.variantId === String(variantId)
      ) {
        count += 1;
      }
    }
    return count;
  }

  /**
   * Units a variant must have in stock to be selectable for one copy: this copy
   * (1) plus whatever the other copies have already claimed of it.
   * @param {number} step
   * @param {string} productId
   * @param {string|number} variantId
   * @returns {number}
   */
  #requiredUnitsForVariant(step, productId, variantId) {
    return 1 + this.#committedDemandForVariant(step, productId, variantId);
  }

  /**
   * Inventory-LEVEL availability: a variant is purchasable only if it can supply
   * the units the bundle will request. Falls back to "available" when stock data
   * is missing or inventory isn't tracked, leaving the server 422 as a backstop.
   * @param {any} variant
   * @param {number} requiredUnits
   * @param {{q:number,p:string} | undefined} stockEntry
   * @returns {boolean}
   */
  #isVariantPurchasable(variant, requiredUnits, stockEntry) {
    if (variant.available === false) return false;
    if (variant.inventory_management == null) return true; // untracked = unlimited
    if (!stockEntry) return true; // stock map missing = fail open
    if (stockEntry.p === "continue") return true; // overselling allowed
    return (stockEntry.q ?? 0) >= requiredUnits;
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {Map<number, string>} selectedOptions
   * @returns {boolean}
   */
  #hasMatchingAvailableVariant(step, productId, selectedOptions) {
    const variantKey = `${step}-${productId}`;
    const variants = this.#variantData.get(variantKey) || [];
    const stockMap = this.#stockData.get(variantKey) || {};

    return variants.some((variant) => {
      const matchesSelection = Array.from(selectedOptions.entries()).every(
        ([optionPosition, optionValue]) =>
          variant.options[optionPosition - 1] === optionValue,
      );
      if (!matchesSelection) return false;

      const requiredUnits = this.#requiredUnitsForVariant(
        step,
        productId,
        variant.id,
      );
      return this.#isVariantPurchasable(
        variant,
        requiredUnits,
        stockMap[variant.id],
      );
    });
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {number} optionPosition
   * @param {string} optionValue
   * @returns {boolean}
   */
  #hasAvailableOptionValue(step, productId, optionPosition, optionValue) {
    const variantKey = `${step}-${productId}`;
    const variants = this.#variantData.get(variantKey) || [];

    return variants.some((variant) => {
      if (variant.available === false) return false;

      return variant.options[optionPosition - 1] === optionValue;
    });
  }

  /**
   * Whether an option VALUE (e.g. a colour) has any variant still purchasable for
   * this copy, ignoring the copy's other selected options. Used for colour
   * swatches so picking a colour never gets blocked just because the currently
   * selected size is sold out in it — the size group then narrows to that colour.
   * @param {number} step
   * @param {string} productId
   * @param {number} optionPosition
   * @param {string} optionValue
   * @returns {boolean}
   */
  #hasPurchasableVariantForOptionValue(
    step,
    productId,
    optionPosition,
    optionValue,
  ) {
    const variantKey = `${step}-${productId}`;
    const variants = this.#variantData.get(variantKey) || [];
    const stockMap = this.#stockData.get(variantKey) || {};

    return variants.some((variant) => {
      if (variant.options[optionPosition - 1] !== optionValue) return false;

      const requiredUnits = this.#requiredUnitsForVariant(
        step,
        productId,
        variant.id,
      );
      return this.#isVariantPurchasable(
        variant,
        requiredUnits,
        stockMap[variant.id],
      );
    });
  }

  /**
   * @param {HTMLElement} productContainer
   * @returns {Map<number, string>}
   */
  #getSelectedOptionMap(productContainer) {
    const selectedOptions = new Map();
    const checkedInputs = productContainer.querySelectorAll(
      'input[type="radio"]:checked',
    );

    for (const input of checkedInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (input.disabled) continue;

      const optionLabel = input.closest(".fts-bundle__option-label");
      if (optionLabel instanceof HTMLElement && optionLabel.hidden) continue;

      const optionPosition = parseInt(input.dataset.optionPosition || "", 10);
      if (Number.isNaN(optionPosition)) continue;

      selectedOptions.set(optionPosition, input.value);
    }

    return selectedOptions;
  }

  /**
   * @param {HTMLElement} productContainer
   * @returns {string[]}
   */
  #getSelectedOptions(productContainer) {
    return Array.from(this.#getSelectedOptionMap(productContainer).entries())
      .sort(([leftPosition], [rightPosition]) => leftPosition - rightPosition)
      .map(([, optionValue]) => optionValue);
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {string[]} selectedOptions
   * @returns {any | undefined}
   */
  #getMatchingVariant(step, productId, selectedOptions) {
    const variantKey = `${step}-${productId}`;
    const variants = this.#variantData.get(variantKey) || [];
    const stockMap = this.#stockData.get(variantKey) || {};

    return variants.find((variant) => {
      const matchesSelection =
        variant.options.length === selectedOptions.length &&
        variant.options.every(
          (option, index) => option === selectedOptions[index],
        );
      if (!matchesSelection) return false;

      const requiredUnits = this.#requiredUnitsForVariant(
        step,
        productId,
        variant.id,
      );
      return this.#isVariantPurchasable(
        variant,
        requiredUnits,
        stockMap[variant.id],
      );
    });
  }

  /**
   * Whether every option value of a variant maps to a rendered, non-hidden input
   * the shopper can actually see/re-pick (swatch colours can be hidden when an
   * entire colour is unavailable).
   * @param {HTMLElement} productContainer
   * @param {any} variant
   * @returns {boolean}
   */
  #variantOptionsSelectable(productContainer, variant) {
    const optionInputs = Array.from(
      productContainer.querySelectorAll(
        '.fts-bundle__option-label input[type="radio"]',
      ),
    );

    return variant.options.every((value, index) => {
      const optionPosition = index + 1;
      const input = optionInputs.find(
        (candidate) =>
          candidate instanceof HTMLInputElement &&
          parseInt(candidate.dataset.optionPosition || "", 10) ===
            optionPosition &&
          candidate.value === value,
      );

      if (!(input instanceof HTMLInputElement)) return false;

      const label = input.closest(".fts-bundle__option-label");
      return !(label instanceof HTMLElement && label.hidden);
    });
  }

  /**
   * Steps grouped by product id, for multi-variant products only (single-variant
   * items use the hidden-input auto-select path).
   * @returns {Map<string, number[]>}
   */
  #getMultiVariantProductSteps() {
    const groups = new Map();
    const containers = this.querySelectorAll(".fts-bundle__product");
    for (const container of containers) {
      if (!(container instanceof HTMLElement)) continue;
      if (!container.querySelector('input[type="radio"]')) continue;
      const step = parseInt(container.dataset.step || "", 10);
      const productId = container.dataset.productId;
      if (Number.isNaN(step) || !productId) continue;
      if (!groups.has(productId)) groups.set(productId, []);
      groups.get(productId).push(step);
    }
    return groups;
  }

  /**
   * Resolve the variant a container's checked radios currently point at (the
   * merchant's rendered default, read before any availability refresh).
   * @param {HTMLElement} container
   * @param {Array} variants
   * @returns {any | null}
   */
  #getRenderedDefaultVariant(container, variants) {
    const selected = this.#getSelectedOptions(container);
    if (!selected.length) return null;
    return (
      variants.find(
        (variant) =>
          variant.options.length === selected.length &&
          variant.options.every((option, index) => option === selected[index]),
      ) || null
    );
  }

  /**
   * Check the radios in a container so they resolve to the given variant.
   * @param {HTMLElement} container
   * @param {any} variant
   */
  #selectVariantInContainer(container, variant) {
    const optionGroups = container.querySelectorAll(
      ".fts-bundle__option-group",
    );
    for (const optionGroup of optionGroups) {
      const inputs = optionGroup.querySelectorAll('input[type="radio"]');
      for (const input of inputs) {
        if (!(input instanceof HTMLInputElement)) continue;
        const optionPosition = parseInt(input.dataset.optionPosition || "", 10);
        if (Number.isNaN(optionPosition)) continue;
        input.checked = input.value === variant.options[optionPosition - 1];
      }
      this.#updateSelectedValueDisplay(optionGroup);
    }
  }

  /**
   * Point every copy of a product at one shared, in-stock default variant.
   * Prefers the merchant's rendered default when it has enough stock for all
   * copies; otherwise the first variant that does. Leaves the merchant defaults
   * untouched if no single variant can supply every copy (the add-time 422 stays
   * as the backstop for that rare case). Users can still change any copy to a
   * scarcer variant afterwards — availability is enforced per-variant live.
   * @param {string} productId
   * @param {number[]} steps
   */
  #applyUniformDefault(productId, steps) {
    if (!steps.length) return;

    const variantKey = `${steps[0]}-${productId}`;
    const variants = this.#variantData.get(variantKey) || [];
    const stockMap = this.#stockData.get(variantKey) || {};
    if (!variants.length) return;

    const firstContainer = this.#getProductContainer(steps[0], productId);
    if (!(firstContainer instanceof HTMLElement)) return;

    const copies = steps.length;
    const canSupplyAll = (variant) =>
      this.#isVariantPurchasable(variant, copies, stockMap[variant.id]) &&
      this.#variantOptionsSelectable(firstContainer, variant);

    const renderedDefault = this.#getRenderedDefaultVariant(
      firstContainer,
      variants,
    );
    const target =
      renderedDefault && canSupplyAll(renderedDefault)
        ? renderedDefault
        : variants.find(canSupplyAll);

    if (!target) return;

    for (const step of steps) {
      const container = this.#getProductContainer(step, productId);
      if (container instanceof HTMLElement) {
        this.#selectVariantInContainer(container, target);
      }
    }
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {{ updateUi?: boolean }} [options]
   */
  #syncProductState(step, productId, { updateUi = true } = {}) {
    const productContainer = this.#getProductContainer(step, productId);
    if (!productContainer) return;

    const selectedOptions = this.#getSelectedOptions(productContainer);
    const optionGroupCount = productContainer.querySelectorAll(
      ".fts-bundle__option-group",
    ).length;

    if (selectedOptions.length < optionGroupCount) {
      this.#clearSelection(step, productContainer, { updateUi });
      return;
    }

    const matchingVariant = this.#getMatchingVariant(
      step,
      productId,
      selectedOptions,
    );

    if (!matchingVariant) {
      this.#setInvalidSelection(step, productId, productContainer, {
        updateUi,
      });
      return;
    }

    this.#applySelection(step, productId, productContainer, matchingVariant, {
      updateUi,
    });
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {HTMLElement} productContainer
   * @param {{ updateUi?: boolean }} [options]
   */
  #setInvalidSelection(
    step,
    productId,
    productContainer,
    { updateUi = true } = {},
  ) {
    this.#selections.delete(step);
    this.#invalidSelections.set(step, {
      productId,
      reason: "Selected combination is unavailable",
    });
    this.#updateInvalidSelectionStyles(productContainer, true);

    if (updateUi) {
      this.#restoreProductImage(productContainer);
      this.#restoreProductPriceDisplay(productContainer);
    }

    this.#updatePriceDisplay();
    this.#updateButtonState();
  }

  /**
   * @param {number} step
   * @param {HTMLElement} productContainer
   */
  #clearInvalidSelection(step, productContainer) {
    this.#invalidSelections.delete(step);
    this.#updateInvalidSelectionStyles(productContainer, false);
  }

  /**
   * @param {HTMLElement} productContainer
   * @param {boolean} isInvalid
   */
  #updateInvalidSelectionStyles(productContainer, isInvalid) {
    const labels = productContainer.querySelectorAll(
      ".fts-bundle__option-label",
    );
    for (const label of labels) {
      if (!(label instanceof HTMLElement)) continue;
      label.classList.remove("is-invalid-selection");
    }

    if (!isInvalid) return;

    const checkedInputs = productContainer.querySelectorAll(
      '.fts-bundle__options input[type="radio"]:checked',
    );

    for (const input of checkedInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const label = input.closest(".fts-bundle__option-label");
      if (label instanceof HTMLElement) {
        label.classList.add("is-invalid-selection");
      }
    }
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @param {HTMLElement} productContainer
   * @param {any} variant
   * @param {{ updateUi?: boolean }} [options]
   */
  #applySelection(
    step,
    productId,
    productContainer,
    variant,
    { updateUi = true } = {},
  ) {
    this.#clearInvalidSelection(step, productContainer);

    const productTitle =
      productContainer
        .querySelector(".fts-bundle__product-title")
        ?.textContent?.trim() || "";

    this.#selections.set(step, {
      variantId: String(variant.id),
      productTitle,
      variantTitle: variant.title,
      productId,
      imageUrl: variant.featured_image?.src || "",
      price: variant.price || 0,
      compareAtPrice: variant.compare_at_price || variant.price || 0,
    });

    if (updateUi) {
      this.#updateProductImage(
        productContainer,
        variant.featured_image?.src || "",
        variant.featured_image?.alt || "",
      );
      this.#updateProductPriceDisplay(step, productId);
    }

    this.#updatePriceDisplay();
    this.#updateButtonState();
  }

  /**
   * @param {number} step
   * @param {HTMLElement} productContainer
   * @param {{ updateUi?: boolean }} [options]
   */
  #clearSelection(step, productContainer, { updateUi = true } = {}) {
    this.#selections.delete(step);
    this.#clearInvalidSelection(step, productContainer);

    if (updateUi) {
      this.#restoreProductImage(productContainer);
      this.#restoreProductPriceDisplay(productContainer);
    }

    this.#updatePriceDisplay();
    this.#updateButtonState();
  }

  /**
   * @param {HTMLElement} productContainer
   * @param {string} imageSrc
   * @param {string} imageAlt
   */
  #updateProductImage(productContainer, imageSrc, imageAlt) {
    const img = productContainer.querySelector(
      ".fts-bundle__product-media img",
    );
    if (!(img instanceof HTMLImageElement)) return;

    if (!imageSrc) {
      this.#restoreProductImage(productContainer);
      return;
    }

    img.src = imageSrc;
    img.alt = imageAlt || productContainer.dataset.initialImageAlt || img.alt;
  }

  /**
   * @param {HTMLElement} productContainer
   */
  #restoreProductImage(productContainer) {
    const img = productContainer.querySelector(
      ".fts-bundle__product-media img",
    );
    if (!(img instanceof HTMLImageElement)) return;

    const initialImageSrc = productContainer.dataset.initialImageSrc;
    if (!initialImageSrc) return;

    img.src = initialImageSrc;
    img.alt = productContainer.dataset.initialImageAlt || img.alt;
  }

  /**
   * @param {HTMLElement} priceContainer
   * @param {number} price
   * @param {number} compareAtPrice
   */
  #setPriceDisplay(priceContainer, price, compareAtPrice) {
    const compareEl = priceContainer.querySelector(
      ".fts-bundle__price--compare",
    );
    const saleEl = priceContainer.querySelector(".fts-bundle__price--sale");

    if (compareEl instanceof HTMLElement) {
      if (compareAtPrice > price) {
        compareEl.textContent = this.#formatMoney(compareAtPrice);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = "";
        compareEl.hidden = true;
      }
    }

    if (saleEl instanceof HTMLElement) {
      saleEl.textContent = this.#formatMoney(price);
    }
  }

  /**
   * Update individual product price display
   * @param {number} step
   * @param {string} productId
   */
  #updateProductPriceDisplay(step, productId) {
    const priceContainer = this.querySelector(
      `[data-product-price="${step}-${productId}"]`,
    );
    if (!(priceContainer instanceof HTMLElement)) return;

    const selection = this.#selections.get(step);
    if (!selection || selection.productId !== productId) return;

    const { compareAtPrice, salePrice } = this.#getSelectionDisplayPrices(
      step,
      selection,
    );

    this.#setPriceDisplay(priceContainer, salePrice, compareAtPrice);
  }

  /**
   * @param {HTMLElement} productContainer
   */
  #restoreProductPriceDisplay(productContainer) {
    const priceContainer = productContainer.querySelector(
      "[data-product-price]",
    );
    if (!(priceContainer instanceof HTMLElement)) return;

    const initialPrice =
      parseInt(priceContainer.dataset.initialPrice || "", 10) || 0;
    const initialCompareAtPrice =
      parseInt(priceContainer.dataset.initialCompareAtPrice || "", 10) || 0;
    const displayDiscountPercent =
      this.#getDisplayDiscountPercent(productContainer);

    if (displayDiscountPercent > 0) {
      this.#setPriceDisplay(
        priceContainer,
        this.#calculateDisplayPrice(initialPrice, displayDiscountPercent),
        initialPrice,
      );
      return;
    }

    this.#setPriceDisplay(priceContainer, initialPrice, initialCompareAtPrice);
  }

  /**
   * Restore checked radios and selected-value labels to their initial state
   */
  #restoreOptionSelections() {
    const radios = this.querySelectorAll('input[type="radio"]');
    for (const radio of radios) {
      if (!(radio instanceof HTMLInputElement)) continue;
      radio.checked = radio.defaultChecked;
    }

    const fieldsets = this.querySelectorAll(".fts-bundle__option-group");
    for (const fieldset of fieldsets) {
      this.#updateSelectedValueDisplay(fieldset);
    }
  }

  /**
   * Restore images and prices to the initial rendered state
   */
  #restoreInitialProductState() {
    const productContainers = this.querySelectorAll(".fts-bundle__product");
    for (const productContainer of productContainers) {
      if (!(productContainer instanceof HTMLElement)) continue;
      this.#restoreProductImage(productContainer);
      this.#restoreProductPriceDisplay(productContainer);
    }
  }

  /**
   * Calculate total price from all selections
   * @returns {number} Total price in cents
   */
  #calculateTotalPrice() {
    let total = 0;
    for (const [, selection] of this.#selections) {
      total += selection.price || 0;
    }
    return total;
  }

  /**
   * Calculate total compare-at price from all selections
   * @returns {number} Total compare-at price in cents
   */
  #calculateCompareAtPrice() {
    let total = 0;
    for (const [, selection] of this.#selections) {
      total += selection.compareAtPrice || selection.price || 0;
    }
    return total;
  }

  /**
   * @param {number} discountPercent
   * @returns {number}
   */
  #clampDiscountPercent(discountPercent) {
    return Math.max(0, Math.min(100, discountPercent));
  }

  /**
   * @param {HTMLElement | null} productContainer
   * @returns {number}
   */
  #getDisplayDiscountPercent(productContainer) {
    if (!(productContainer instanceof HTMLElement)) return 0;

    const discountPercent = parseInt(
      productContainer.dataset.displayDiscountPercent || "",
      10,
    );

    if (Number.isNaN(discountPercent)) return 0;

    return this.#clampDiscountPercent(discountPercent);
  }

  /**
   * @param {number} actualPrice
   * @param {number} discountPercent
   * @returns {number}
   */
  #calculateDisplayPrice(actualPrice, discountPercent) {
    const clampedDiscountPercent = this.#clampDiscountPercent(discountPercent);

    return Math.max(
      0,
      Math.round((actualPrice * (100 - clampedDiscountPercent)) / 100),
    );
  }

  /**
   * @param {number} step
   * @param {BundleSelection} selection
   * @returns {{ compareAtPrice: number, salePrice: number }}
   */
  #getSelectionDisplayPrices(step, selection) {
    const productContainer = this.#getProductContainer(
      step,
      selection.productId,
    );
    const displayDiscountPercent =
      this.#getDisplayDiscountPercent(productContainer);

    if (displayDiscountPercent > 0) {
      const basePrice = selection.price || 0;

      return {
        compareAtPrice: basePrice,
        salePrice: this.#calculateDisplayPrice(
          basePrice,
          displayDiscountPercent,
        ),
      };
    }

    return {
      compareAtPrice: selection.compareAtPrice || selection.price || 0,
      salePrice: selection.price || 0,
    };
  }

  /**
   * @returns {number} Total display price in cents
   */
  #calculateDisplayTotalPrice() {
    let total = 0;

    for (const [step, selection] of this.#selections) {
      total += this.#getSelectionDisplayPrices(step, selection).salePrice;
    }

    return total;
  }

  /**
   * @returns {number} Total display compare-at price in cents
   */
  #calculateDisplayCompareAtPrice() {
    let total = 0;

    for (const [step, selection] of this.#selections) {
      total += this.#getSelectionDisplayPrices(step, selection).compareAtPrice;
    }

    return total;
  }

  /**
   * Format cents as currency string
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: Shopify?.currency?.active || "USD",
    }).format(cents / 100);
  }

  /**
   * Update the total price display (header and footer)
   */
  #updatePriceDisplay() {
    const {
      originalPrice,
      comparePrice,
      salePrice,
      saveBadge,
      footerOriginal,
      footerPrice,
    } = this.refs;

    const total = this.#calculateDisplayTotalPrice();
    const compareTotal = this.#calculateDisplayCompareAtPrice();
    const hasDiscount = compareTotal > total;
    const discountPercent =
      hasDiscount && compareTotal > 0
        ? Math.round((1 - total / compareTotal) * 100)
        : 0;
    const promoLabel = this.dataset.promoLabel?.trim() || "";

    if (originalPrice) {
      if (hasDiscount) {
        originalPrice.textContent = this.#formatMoney(compareTotal);
        originalPrice.hidden = false;
      } else {
        originalPrice.textContent = "";
        originalPrice.hidden = true;
      }
    }

    if (comparePrice) {
      comparePrice.hidden = true;
    }

    if (salePrice) {
      salePrice.textContent = this.#formatMoney(total);
    }

    if (saveBadge) {
      if (promoLabel) {
        saveBadge.textContent = promoLabel;
        saveBadge.hidden = false;
      } else if (hasDiscount && discountPercent > 0) {
        saveBadge.textContent = `Save Up To ${discountPercent}%`;
        saveBadge.hidden = false;
      } else {
        saveBadge.textContent = "";
        saveBadge.hidden = true;
      }
    }

    if (footerOriginal) {
      if (hasDiscount) {
        footerOriginal.textContent = this.#formatMoney(compareTotal);
        footerOriginal.hidden = false;
      } else {
        footerOriginal.textContent = "";
        footerOriginal.hidden = true;
      }
    }

    if (footerPrice) {
      footerPrice.textContent = this.#formatMoney(total);
    }
  }

  /**
   * Update add-to-cart button state
   */
  #updateButtonState() {
    const { addToCartButton, validationMessage } = this.refs;
    if (!addToCartButton) return;

    const allSelected =
      this.#totalProducts > 0 &&
      this.#selections.size === this.#totalProducts &&
      this.#invalidSelections.size === 0;

    if (allSelected) {
      addToCartButton.disabled = false;
      if (validationMessage) validationMessage.textContent = "";
      return;
    }

    addToCartButton.disabled = true;

    if (!validationMessage) return;

    if (this.#totalProducts === 0) {
      validationMessage.textContent = "Add products to build your bundle.";
      return;
    }

    if (this.#invalidSelections.size > 0) {
      validationMessage.textContent =
        "The selected color/size combination is unavailable. Please adjust your bundle.";
      return;
    }

    const remaining = this.#totalProducts - this.#selections.size;
    validationMessage.textContent = `Select ${remaining} more product${
      remaining > 1 ? "s" : ""
    } to complete your bundle`;
  }

  /**
   * Handle add bundle to cart
   */
  async handleAddToCart() {
    const { addToCartButton, validationMessage } = this.refs;
    if (!addToCartButton || addToCartButton.disabled) return;

    const bundleIdBase = this.dataset.sectionId || "bundle";
    const bundleId = `${bundleIdBase}-${Date.now()}`;
    const totalPriceCents = this.#calculateTotalPrice();
    const totalProducts = String(this.#totalProducts);
    const orderedSelections = Array.from(this.#selections.entries()).sort(
      ([leftStep], [rightStep]) => leftStep - rightStep,
    );

    addToCartButton.disabled = true;

    const textSpan = addToCartButton.querySelector("span");
    const originalText = textSpan?.textContent || "Add Bundle to Cart";
    if (textSpan) textSpan.textContent = "Adding...";

    let didSucceed = false;

    try {
      const componentVariantIds = orderedSelections.map(
        ([, selection]) => selection.variantId,
      );

      const items = orderedSelections.map(([step, selection]) => ({
        id: selection.variantId,
        quantity: 1,
        properties: {
          _bundle_id: bundleId,
          _bundle_total_price_cents: String(totalPriceCents),
          _bundle_step: String(step),
          _bundle_total_steps: totalProducts,
          _bundle_component_ids: JSON.stringify(componentVariantIds),
        },
      }));

      if (!items.length) {
        throw new Error("No bundle items selected");
      }

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig("json"),
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      // Shopify returns a non-OK status (e.g. 422) with the specific reason in
      // `description` — most often a single line item that's sold out or below
      // the requested quantity (a BOGO bundle requests several of each
      // variant). Surface that reason so shoppers know to pick a different
      // size/colour, instead of a generic "failed" that reads as a broken
      // button. Read the body before the ok-check so the message isn't lost.
      if (!response.ok || data.status) {
        const reason =
          data.description ||
          data.message ||
          "Couldn't add this bundle to cart. Please adjust your selection.";
        console.error("Cart add error:", reason);
        if (validationMessage) validationMessage.textContent = reason;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.dataset.sectionId, {
          source: "fts-bundle-builder",
          itemCount: items.length,
        }),
      );

      this.#resetForm();
      didSucceed = true;
    } catch (error) {
      console.error("Cart add failed:", error);
      if (validationMessage) {
        validationMessage.textContent = "Failed to add bundle to cart";
      }
    } finally {
      if (textSpan) textSpan.textContent = originalText;
      if (!didSucceed) {
        addToCartButton.disabled = false;
      }
    }
  }

  /**
   * Reset form after successful add
   */
  #resetForm() {
    this.#restoreOptionSelections();
    this.#restoreInitialProductState();
    this.#initializeSelections({ updateUi: true });
    this.#updatePriceDisplay();
    this.#updateButtonState();
  }
}

customElements.define("fts-bundle-builder", FtsBundleBuilder);
